import { Search, X } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { Input } from "@/app/components/input";
import type { Ad } from "@/app/components/ad-card";

interface HomeSearchBarProps {
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  onSearch: () => void;
  ads: Ad[];
}

export function HomeSearchBar({
  searchQuery,
  onSearchQueryChange,
  onSearch,
  ads,
}: HomeSearchBarProps) {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchContainerRef = useRef<HTMLDivElement>(null);

  // Minimum 3 characters required
  const isSearchValid = searchQuery.trim().length >= 3;

  // Generate suggestions based on current query
  const suggestions = !isSearchValid ? [] : [
    // Get unique product titles that match
    ...new Set(
      ads
        .filter((ad) =>
          ad.title.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .map((ad) => ad.title)
        .slice(0, 5)
    ),
    // Get unique categories that match
    ...new Set(
      ads
        .filter((ad) =>
          ad.category.toLowerCase().includes(searchQuery.toLowerCase())
        )
        .map((ad) => ad.category)
        .slice(0, 3)
    ),
  ].slice(0, 6);

  // Handle click outside to close suggestions
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleInputChange = (value: string) => {
    onSearchQueryChange(value);
    setShowSuggestions(value.trim().length >= 3);
  };

  const handleSuggestionClick = (suggestion: string) => {
    onSearchQueryChange(suggestion);
    setShowSuggestions(false);
    // Only search if valid
    if (suggestion.trim().length >= 3) {
      onSearch();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && isSearchValid) {
      setShowSuggestions(false);
      onSearch();
    }
  };

  const handleClearSearch = () => {
    onSearchQueryChange("");
    setShowSuggestions(false);
  };

  return (
    <div className="bg-card border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="relative" ref={searchContainerRef}>
          <div className="relative flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground pointer-events-none" />
              <Input
                type="text"
                placeholder="O que você está procurando?"
                className="pl-12 pr-10"
                value={searchQuery}
                onChange={(e) => handleInputChange(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  if (searchQuery.trim() !== "") {
                    setShowSuggestions(true);
                  }
                }}
              />
              {searchQuery && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-secondary rounded-full transition-colors"
                  aria-label="Limpar busca"
                >
                  <X className="w-4 h-4 text-muted-foreground" />
                </button>
              )}
            </div>
            <button
              onClick={() => {
                if (isSearchValid) {
                  setShowSuggestions(false);
                  onSearch();
                }
              }}
              disabled={!isSearchValid}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                isSearchValid
                  ? "bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer"
                  : "bg-secondary text-muted-foreground cursor-not-allowed opacity-60"
              }`}
              title={!isSearchValid ? "Digite pelo menos 3 caracteres" : ""}
            >
              Buscar
            </button>
          </div>

          {/* Suggestions Dropdown */}
          {showSuggestions && suggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-card border border-border rounded-lg shadow-lg overflow-hidden z-50">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full px-4 py-3 text-left hover:bg-secondary/50 transition-colors border-b border-border last:border-b-0 flex items-center gap-3"
                >
                  <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  <span className="text-sm text-foreground truncate">
                    {suggestion}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}